/**
 * Creating an agency, in two steps.
 *
 * **Two shapes, not one form with things hidden.** Somebody arriving from Google is already
 * identified — showing them an email box, a password box and another Google button is asking them
 * to do again what they have just done, and it reads as though the sign-in failed. They are asked
 * only for what is genuinely still missing.
 *
 * **Two steps, because they are two different questions.** Who you are, then what your agency is
 * called. One column of five fields is a wall; split, each step is answerable without scrolling,
 * and the second has a name on it — which is where saying what the agency name is used for
 * actually helps.
 *
 * **The Google path has one step, not two.** Google has already given the name and the address, so
 * splitting what is left — an agency name and a phone number — into two screens would be a step
 * bar counting to two above a single question. Two steps is a courtesy to a long form, not a
 * ritual.
 *
 * **Nothing is created until the last step.** The first validates in the browser and moves on;
 * only "Finish setting up" writes anything. A half-made agency is worse than none.
 *
 * The address and identity are not carried in the URL. `/auth/google` hands back an opaque,
 * single-use proof; the address shown here is for reassurance only, and the server reads the real
 * one off its own challenge when the form is submitted.
 */

import { useState, type FormEvent } from "react";
import { MODULES, MODULE_INFO, type ModuleCode } from "@rentorng/core";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { Alert, Button, Card, Field, Input } from "@/components/ui";
import { PhoneInput } from "@/components/phone-input";
import { PasswordInput } from "@/components/password-input";
import { GoogleButton } from "@/components/google-button";
import { AuthShell } from "./AuthShell";
import { usePageTitle } from "@/lib/title";

/** What Google confirmed, held in memory for the length of the form and never in the URL. */
interface GoogleHandoff {
  signupToken: string;
  email: string;
  name: string;
}

/** Everything the two steps collect between them. */
interface Draft {
  name: string;
  email: string;
  password: string;
  agency: string;
  phone: string;
  /** What this business does. At least one; both is a real answer and not an edge case. */
  modules: ModuleCode[];
}

/**
 * Signed in, and belonging to no agency.
 *
 * A `users` row is not by itself a member of anything. Three kinds of account have one and no
 * membership: a **client login** — a tenant, an owner or a buyer who set a password on the portal
 * (ADR-0102) — a **console-only account** for platform staff (ADR-0098), and somebody whose
 * membership has since been removed.
 *
 * All three could sign in at this door and then reach nothing: `orgContext()` resolves no
 * organisation, so every data route answers 403 and the application draws a shell with nothing in
 * it. Worse, `AppLayout` sends an org-less session here and this screen used to send it straight
 * back to `/app` — two redirects pointing at each other.
 *
 * **Refusing membership-less accounts at the API door is not the fix**, because a console account
 * is legitimate and would go with them. It is handled here instead: say which kind of account this
 * is and point at the door it actually opens. Creating an agency stays available underneath,
 * because somebody whose membership was removed has an ordinary reason to want one.
 */
function WrongDoor({ consoleUrl, onCreateAgency }: { consoleUrl: string; onCreateAgency: () => void }) {
  const navigate = useNavigate();

  const signOut = useApiMutation({
    mutationFn: () => api.post("/auth/sign-out"),
    invalidates: [["*"]],
    onSuccess: () => navigate("/sign-in", { replace: true }),
  });

  return (
    <AuthShell>
      <Card>
        <h1 className="text-xl font-semibold">This account is not on an agency</h1>
        {consoleUrl ? (
          <>
            <p className="mt-1 text-sm text-ink-500">
              It signs in to the console rather than to an agency's own application. There is nothing here for it to
              open.
            </p>
            <a href={consoleUrl} className="btn btn-primary mt-5 w-full">
              Open the console
            </a>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-ink-500">
              It looks like a client login — for a tenant, an owner or a buyer. Your tenancy, your purchase and your
              receipts are in the client area, not in the agency application.
            </p>
            <Link to="/portal" className="btn btn-primary mt-5 w-full">
              Go to your account
            </Link>
          </>
        )}

        <div className="mt-5 border-t border-ink-100 pt-4 text-sm text-ink-500">
          <p>
            Setting up an agency of your own?{" "}
            <button type="button" className="link" onClick={onCreateAgency}>
              Create one on this account
            </button>
          </p>
          <p className="mt-2">
            Or{" "}
            <button type="button" className="link" onClick={() => signOut.mutate(undefined)}>
              sign in as somebody else
            </button>
            .
          </p>
        </div>
      </Card>
    </AuthShell>
  );
}

export function SignUpPage() {
  usePageTitle("Create an account");
  const { user, org, orgs, consoleUrl, refresh, loading } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Set when an account with no organisation has said it wants one anyway.
   *
   * Somebody whose membership was removed, or a client login whose owner really is starting an
   * agency. Without it `WrongDoor` would be a dead end for a case that is uncommon but entirely
   * legitimate.
   */
  const [claimingAgency, setClaimingAgency] = useState(false);

  const handedOver = (location.state as { google?: GoogleHandoff } | null)?.google ?? null;
  const [google, setGoogle] = useState<GoogleHandoff | null>(handedOver);

  /**
   * Somebody back from Google is already past step one — everything it asks for is known. They
   * start on the only question left, and the step bar stays out of it.
   */
  const [step, setStep] = useState<1 | 2>(handedOver ? 2 : 1);
  const [draft, setDraft] = useState<Draft>({
    name: handedOver?.name ?? "",
    email: handedOver?.email ?? "",
    password: "",
    agency: "",
    /* Letting to begin with because it is what most sign-ups are — but it is a *pre-selection on a
       question that is asked*, not an assumption nobody is shown. Somebody who only sells plots
       unticks it. */
    modules: ["letting"],
    phone: "",
  });
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));

  const signUp = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.postAsHuman("/auth/sign-up", input, "sign_up"),
    invalidates: [["me"]],
    onSuccess: async () => {
      await refresh();
      /**
       * Land somewhere that says it worked.
       *
       * Dropping straight onto an empty dashboard leaves somebody wondering whether the account
       * was created at all. `?welcome=1` is read once, by the dashboard, and says so plainly.
       */
      navigate("/app?welcome=1", { replace: true });
    },
  });

  /*
    Somebody already signed in.

    With an organisation they belong in the application, as before. Without one there is nothing for
    them to open, and sending them to `/app` would bounce them straight back here — see `WrongDoor`.
  */
  if (!loading && user && (org || orgs.length > 0)) return <Navigate to="/app" replace />;
  if (!loading && user && !claimingAgency) return <WrongDoor consoleUrl={consoleUrl} onCreateAgency={() => setClaimingAgency(true)} />;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    signUp.mutate({
      name: draft.name,
      agency: draft.agency,
      phone: draft.phone,
      modules: draft.modules,
      ...(google ? { googleToken: google.signupToken } : { email: draft.email, password: draft.password }),
    });
  };

  const advance = (event: FormEvent<HTMLFormElement>) => {
    // `required`, the email shape and the minimum password length are enforced by the browser
    // before this runs.
    event.preventDefault();
    setStep(2);
  };

  const heading = google
    ? `Almost there, ${google.name.split(" ")[0] || "there"}`
    : step === 1
      ? "Create your agency"
      : "About your agency";

  // Sending the name with the agency, on the single Google step, rather than from a step one that
  // is skipped.
  const nameOnStepTwo = Boolean(google);

  return (
    <AuthShell>
      <Card>
        {/*
          The bar appears once there is progress to show.
          On the first screen "Step 1 of 2" is only an announcement that this will take two
          screens, which is a reason to leave rather than a reason to carry on. It shows up when
          somebody has actually moved — where it is telling them how much is left, not how much
          there is.
        */}
        {google || step === 1 ? null : <Steps step={step} />}

        <h1 className={`${google || step === 1 ? "" : "mt-3 "}text-xl font-semibold`}>{heading}</h1>
        {google ? (
          <p className="mt-1 text-sm text-ink-500">
            Signed in as <strong>{google.email}</strong>.
          </p>
        ) : null}

        {signUp.errorMessage ? (
          <div className="mt-4">
            <Alert tone="danger">{signUp.errorMessage}</Alert>
          </div>
        ) : null}

        {step === 1 ? (
          <form onSubmit={advance} className="mt-5 space-y-4">
            <Field label="Your name" htmlFor="name" required error={signUp.fieldErrors.name}>
              <Input
                id="name"
                name="name"
                required
                autoFocus
                placeholder="Tunde Bakare"
                value={draft.name}
                onChange={(event) => set({ name: event.target.value })}
              />
            </Field>

            {google ? null : (
              <>
                <Field label="Email address" htmlFor="email" required error={signUp.fieldErrors.email}>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="you@agency.com"
                    value={draft.email}
                    onChange={(event) => set({ email: event.target.value })}
                  />
                </Field>

                <Field
                  label="Password"
                  htmlFor="password"
                  required
                  error={signUp.fieldErrors.password}
                  hint="At least 8 characters."
                >
                  <PasswordInput
                    id="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    placeholder="Something only you know"
                    value={draft.password}
                    onChange={(event) => set({ password: event.target.value })}
                  />
                </Field>
              </>
            )}

            <Button type="submit" className="w-full">
              Continue
            </Button>
          </form>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            {nameOnStepTwo ? (
              <Field label="Your name" htmlFor="name" required error={signUp.fieldErrors.name}>
                <Input
                  id="name"
                  name="name"
                  required
                  placeholder="Tunde Bakare"
                  value={draft.name}
                  onChange={(event) => set({ name: event.target.value })}
                />
              </Field>
            ) : null}

            <Field
              label="Agency name"
              htmlFor="agency"
              required
              error={signUp.fieldErrors.agency}
              hint="Shown to your clients on invoices and receipts. It can be changed later."
            >
              <Input
                id="agency"
                name="agency"
                required
                autoFocus={!nameOnStepTwo}
                placeholder="Broadview Estates"
                value={draft.agency}
                onChange={(event) => set({ agency: event.target.value })}
              />
            </Field>

            {/*
              What the business does — asked, not assumed.

              This decides which half of the product they open on, and until it was asked everybody
              got the letting dashboard: somebody who only sells plots signed up and was shown a
              rent roll they will never have. Both is a real answer, so these are tickboxes rather
              than a choice of one. ADR-0080.
            */}
            <Field
              label="Business type"
              group
              required
              error={signUp.fieldErrors.modules}
              hint="Select both if your organisation does both. This can be changed later."
            >
              <div className="space-y-2">
                {MODULES.map((code) => {
                  const on = draft.modules.includes(code);
                  return (
                    <label
                      key={code}
                      className={`flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] border p-3 ${
                        on ? "border-brand-500 bg-brand-50/50" : "border-ink-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={on}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...draft.modules, code]
                            : draft.modules.filter((entry) => entry !== code);
                          /* Never nothing. Unticking the last one would leave an account with no
                             application at all, so the last tick simply does not come off. */
                          if (next.length) set({ modules: next });
                        }}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{MODULE_INFO[code].question}</span>
                        <span className="block text-xs text-ink-500">{MODULE_INFO[code].blurb}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </Field>

            <Field
              label="Phone"
              htmlFor="phone"
              required
              error={signUp.fieldErrors.phone}
              hint="Appears on every reminder and receipt. For example +234 803 123 4567."
            >
              <PhoneInput id="phone" required onValue={(value) => set({ phone: value })} />
            </Field>

            <Button type="submit" disabled={signUp.isPending} className="w-full">
              {signUp.isPending ? "Setting up…" : "Finish setting up"}
            </Button>

            {nameOnStepTwo ? null : (
              <button type="button" className="text-sm text-ink-500 hover:text-ink-800" onClick={() => setStep(1)}>
                ← Back
              </button>
            )}
          </form>
        )}

        {/* Only on the first step, and never to somebody who has just used it — offering it again
            reads as the sign-in having failed. */}
        {step === 1 && !google ? (
          <GoogleButton
            onSignedIn={async () => {
              await refresh();
              navigate("/app", { replace: true });
            }}
            onNeedsSignUp={(details) => {
              setGoogle(details);
              set({ name: details.name, email: details.email, password: "" });
              setStep(2);
            }}
          />
        ) : null}

        {google ? (
          <button
            type="button"
            className="mt-5 text-sm text-ink-500 hover:text-ink-800"
            onClick={() => {
              setGoogle(null);
              setStep(1);
              set({ name: "", email: "" });
            }}
          >
            Use a different account
          </button>
        ) : (
          <p className="mt-5 text-sm text-ink-500">
            Already have an account?{" "}
            <Link to="/sign-in" className="link">
              Sign in
            </Link>
          </p>
        )}
      </Card>
    </AuthShell>
  );
}

/**
 * Where you are, and how much is left.
 *
 * Two steps is short enough that a progress bar would be overwrought, and long enough that
 * somebody deserves to know the second one is the last.
 */
function Steps({ step }: { step: 1 | 2 }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2].map((entry) => (
        <span
          key={entry}
          className={`h-1.5 flex-1 rounded-full transition-colors ${entry <= step ? "bg-brand-600" : "bg-ink-200"}`}
        />
      ))}
      <span className="ml-1 shrink-0 text-xs font-medium text-ink-500">Step {step} of 2</span>
    </div>
  );
}
